import { useEffect, useState } from "react";
import { AlertTriangle, ShieldCheck, Droplets, Users } from "lucide-react";
import { fetchTeacherWatchlist } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";

const FLAG_LABEL = {
    en: {
        ph_low: "pH low",
        ph_high: "pH high",
        ammonia_high: "Ammonia high",
        nitrite_high: "Nitrite high",
        nitrate_high: "Nitrate high",
        temp_low: "Temp low",
        temp_high: "Temp high",
    },
    es: {
        ph_low: "pH bajo",
        ph_high: "pH alto",
        ammonia_high: "Amoníaco alto",
        nitrite_high: "Nitrito alto",
        nitrate_high: "Nitrato alto",
        temp_low: "Temp baja",
        temp_high: "Temp alta",
    },
};

function relTime(iso, lang) {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    const diffMin = Math.max(1, Math.round((Date.now() - t) / 60000));
    if (diffMin < 60)
        return lang === "es" ? `hace ${diffMin}m` : `${diffMin}m ago`;
    const h = Math.round(diffMin / 60);
    if (h < 24) return lang === "es" ? `hace ${h}h` : `${h}h ago`;
    const d = Math.round(h / 24);
    return lang === "es" ? `hace ${d}d` : `${d}d ago`;
}

export default function WatchlistWidget() {
    const { lang, t } = useLang();
    const [data, setData] = useState(null);
    const labels = FLAG_LABEL[lang === "es" ? "es" : "en"];

    useEffect(() => {
        fetchTeacherWatchlist(7)
            .then(setData)
            .catch(() => setData({ classes: [], total_flags: 0, classes_total: 0 }));
    }, []);

    if (!data) return null;
    const hasClasses = (data.classes_total || 0) > 0;
    const empty = !data.classes || data.classes.length === 0;

    return (
        <section
            className="mt-12"
            data-testid="watchlist-widget"
        >
            <div
                className={
                    "card p-6 md:p-7 relative overflow-hidden border-l-2 " +
                    (empty
                        ? "border-l-[var(--lime)]/60"
                        : "border-l-[var(--coral)]")
                }
            >
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <span className="tag inline-flex items-center gap-1">
                            {empty ? (
                                <ShieldCheck size={12} className="text-[var(--lime)]" />
                            ) : (
                                <AlertTriangle size={12} className="text-[var(--coral)]" />
                            )}
                            {t.dashboard.watchlistTag}
                        </span>
                        <h2
                            className="font-display font-black text-white text-2xl md:text-3xl uppercase mt-2 leading-tight"
                            data-testid="watchlist-title"
                        >
                            {t.dashboard.watchlistHead}
                        </h2>
                        <p className="font-mono text-xs text-[var(--muted)] mt-1">
                            {t.dashboard.watchlistBlurb}
                        </p>
                    </div>
                    {!empty && (
                        <div className="flex items-center gap-2 font-mono text-xs">
                            <span className="px-2 py-1 border border-[var(--coral)] text-[var(--coral)]">
                                {data.total_flags} {t.dashboard.watchlistIssues}
                            </span>
                            <span className="px-2 py-1 border border-[var(--border)] text-[var(--muted)]">
                                {data.classes.length}/{data.classes_total}{" "}
                                {lang === "es" ? "clases" : "classes"}
                            </span>
                        </div>
                    )}
                </div>

                {!hasClasses ? (
                    <p
                        className="font-mono text-sm text-[var(--muted)] mt-5"
                        data-testid="watchlist-no-classes"
                    >
                        {t.dashboard.watchlistNoClasses}
                    </p>
                ) : empty ? (
                    <p
                        className="font-mono text-sm text-[var(--lime)] mt-5"
                        data-testid="watchlist-empty"
                    >
                        {t.dashboard.watchlistEmpty}
                    </p>
                ) : (
                    <ul
                        className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5"
                        data-testid="watchlist-list"
                    >
                        {data.classes.map((c) => {
                            const topFlags = Object.entries(c.flag_counts || {})
                                .sort((a, b) => b[1] - a[1])
                                .slice(0, 3);
                            return (
                                <li
                                    key={c.class_id}
                                    className="border border-[var(--coral)]/40 bg-[var(--coral)]/[0.04] p-4 flex flex-col gap-2"
                                    data-testid={`watchlist-card-${c.class_id}`}
                                >
                                    <div className="flex items-center justify-between gap-3">
                                        <div>
                                            <div className="font-display text-white text-lg leading-tight">
                                                {c.class_name}
                                            </div>
                                            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] mt-1">
                                                #{c.code}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-display font-black text-[var(--coral)] text-2xl leading-none">
                                                {c.issue_count}
                                            </div>
                                            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
                                                {t.dashboard.watchlistIssues}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-2 mt-1">
                                        {topFlags.map(([f, n]) => (
                                            <span
                                                key={f}
                                                className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[var(--coral)]/60 text-[var(--coral)]"
                                            >
                                                {(labels[f] || f.replaceAll("_", " "))} · {n}
                                            </span>
                                        ))}
                                    </div>

                                    <div className="flex items-center gap-3 font-mono text-[11px] text-[var(--muted)] mt-1">
                                        <span className="inline-flex items-center gap-1">
                                            <Users size={11} /> {c.students_affected}{" "}
                                            {t.dashboard.watchlistStudents}
                                        </span>
                                        <span className="inline-flex items-center gap-1">
                                            <Droplets size={11} />{" "}
                                            {t.dashboard.watchlistLatest}:{" "}
                                            <span className="text-white">
                                                {c.latest_student || "—"}
                                            </span>{" "}
                                            · {relTime(c.latest_flag_at, lang)}
                                        </span>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            window.dispatchEvent(
                                                new CustomEvent("flow:select-class", {
                                                    detail: c.class_id,
                                                }),
                                            );
                                            const el =
                                                document.getElementById("classes-section");
                                            if (el)
                                                el.scrollIntoView({
                                                    behavior: "smooth",
                                                    block: "start",
                                                });
                                        }}
                                        className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--cyan)] hover:text-[var(--lime)] mt-2 self-start"
                                        data-testid={`watchlist-open-${c.class_id}`}
                                    >
                                        {t.dashboard.watchlistView}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </section>
    );
}
